import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { MatIcon } from '@angular/material/icon'
import {
  type GroupNode,
  NgDiagramGroupHighlightedDirective,
  type NgDiagramGroupNodeTemplate,
  NgDiagramNodeResizeAdornmentComponent,
  NgDiagramNodeSelectedDirective,
  NgDiagramPortComponent,
} from 'ng-diagram'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { DaydicTypesIconNames, Themes } from 'src/app/shared/enums/enums'
import { CreateSubType } from '../../../models/canvas.model'
import { CanvasTraceStateService } from '../../canvas-trace-state.service'

export interface CreateNodeData {
  create: {
    subType: CreateSubType
    prompt: string
    figureStyle?: string
  }
}

const SUB_TYPE_ICONS: Record<CreateSubType, string> = {
  dictionary: DaydicTypesIconNames.DICTIONARY,
  note: DaydicTypesIconNames.NOTE,
  figure: DaydicTypesIconNames.FIGURE,
  quiz: DaydicTypesIconNames.QUIZ,
}

const SUB_TYPE_LABELS: Record<CreateSubType, string> = {
  dictionary: 'Dictionary',
  note: 'Note',
  figure: 'Figure',
  quiz: 'Quiz',
}

@Component({
  selector: 'ddc-canvas-node-create',
  imports: [
    NgDiagramPortComponent,
    NgDiagramNodeSelectedDirective,
    NgDiagramGroupHighlightedDirective,
    CommonModule,
    NgDiagramNodeResizeAdornmentComponent,
    MatIcon,
  ],
  templateUrl: './create-node.component.html',
  styleUrls: ['./create-node.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.ng-diagram-port-hoverable]': 'true',
  },
})
export class CanvasCreateNodeComponent implements NgDiagramGroupNodeTemplate<CreateNodeData> {
  private themeService = inject(ThemeService)
  private traceStateService = inject(CanvasTraceStateService)
  private theme = toSignal(this.themeService.theme$, { initialValue: Themes.LIGHT })

  node = input.required<GroupNode<CreateNodeData>>()

  private traceResult = computed(() => {
    const nodeId = this.node()?.id
    if (!nodeId) return null
    return this.traceStateService.nodeResults()[nodeId] ?? null
  })

  isSuccess = computed(() => this.traceResult()?.status === 'success')

  createdId = computed(() => this.traceResult()?.created_id ?? null)

  createdType = computed(() => this.traceResult()?.created_type ?? null)

  subTypeIcon = computed(() => {
    const subType = this.node()?.data?.create?.subType
    return subType ? SUB_TYPE_ICONS[subType] : ''
  })

  subTypeLabel = computed(() => {
    const subType = this.node()?.data?.create?.subType
    return subType ? SUB_TYPE_LABELS[subType] : ''
  })

  prompt = computed(() => this.node()?.data?.create?.prompt ?? '')

  figureStyle = computed(() => this.node()?.data?.create?.figureStyle ?? '')

  logoSrc = computed(() => {
    return this.theme() === Themes.DARK
      ? 'assets/media/logos/lingo_logo_dark.png'
      : 'assets/media/logos/lingo_logo_light.png'
  })

  onOpenCreated(event: MouseEvent): void {
    event.stopPropagation()
    const id = this.createdId()
    const type = this.createdType()
    if (id && type) {
      this.traceStateService.showContent(id, type)
    }
  }
}
